import {
  Component,
  HostBinding,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { LoadingDialogComponent } from 'src/app/components';
import {
  BookingService,
  ExamService,
  LayoutService,
  NotificationService,
} from 'src/app/services';
import { WizardStep } from 'src/app/types';
import { BreadcrumbService } from 'xng-breadcrumb';
import { Step1ProfileCheckFormSdvComponent } from '../step1-profile-check-form-sdv';
import { Step2RequirementsCheckFormSdvComponent } from '../step2-requirements-check-form-sdv';
import { Step3RequirementsInfoPageSdvComponent } from '../step3-requirements-info-page-sdv';
import { Step4TestTimeFormSdvComponent } from '../step4-test-time-form-sdv';
import { Step5PricingSdvComponent } from '../step5-pricing-sdv';
import { Step6AdditionalQuestionsFormSdvComponent } from '../step6-additional-questions-form-sdv';
import { Step7BookingOverviewSdvComponent } from '../step7-booking-overview-sdv';

@Component({
  selector: 'app-exam-booking-form-sdv',
  templateUrl: './exam-booking-form-sdv.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class ExamBookingFormSdvComponent implements OnInit {
  @HostBinding('class.form-page-component') hostClass =
    true;

  @ViewChild('step1')
  step1Component!: Step1ProfileCheckFormSdvComponent;
  @ViewChild('step2')
  step2Component!: Step2RequirementsCheckFormSdvComponent;
  @ViewChild('step3')
  step3Component!: Step3RequirementsInfoPageSdvComponent;
  @ViewChild('step4')
  step4Component!: Step4TestTimeFormSdvComponent;
  @ViewChild('step5')
  step5Component!: Step5PricingSdvComponent;
  @ViewChild('step6')
  step6Component!: Step6AdditionalQuestionsFormSdvComponent;
  @ViewChild('step7')
  step7Component!: Step7BookingOverviewSdvComponent;

  isPayBtnClicked = false;
  stepId = '';
  currentStepIndex = 0;

  steps: WizardStep[] = [
    {
      id: 'step-1',
      label: 'label.profile-check',
      isValid: false,
      isRelevant: true,
    },
    {
      id: 'step-2',
      label: 'booking.sdv.prerequisites-check-page.title',
      isValid: false,
      isRelevant: true,
    },
    {
      id: 'step-3',
      label: 'booking.sdv.info-page.title',
      isValid: true,
      isRelevant: true,
    },
    {
      id: 'step-4',
      label: 'label.test-event-select',
      isValid: false,
      isRelevant: true,
    },
    {
      id: 'step-5',
      label: 'booking.sdv.pricing-page.title',
      isValid: false,
      isRelevant: true,
    },
    {
      id: 'step-6',
      label: 'title.additional-questions',
      isValid: true,
      isRelevant: true,
    },
    {
      id: 'step-7',
      label: 'title.summary',
      isValid: false,
      isRelevant: true,
    },
  ];
  stepLoading = false;

  constructor(
    private activatedRoute: ActivatedRoute,
    private bookingService: BookingService,
    private breadcrumbService: BreadcrumbService,
    private examService: ExamService,
    private layoutService: LayoutService,
    private modalService: BsModalService,
    private note: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.set(
      '@examName',
      this.examTestName
    );
  }

  get relevantSteps(): WizardStep[] {
    return this.steps.filter((step) => step.isRelevant);
  }

  async handleNext(): Promise<void> {
    this.stepLoading = true;

    let isValid = false;

    try {
      switch (this.currentStepIndex) {
        case 0:
          isValid = await this.step1Component.submit();
          this.setProfileCheckFormValid(isValid);
          break;

        case 1:
          isValid = await this.step2Component.submit();
          this.setRequirementsCheckFormValid(isValid);
          break;

        case 2:
          isValid = await this.step3Component.submit();
          break;

        case 3:
          isValid = await this.step4Component.submit();
          this.setTimeSlotFormValid(isValid);
          break;

        case 4:
          isValid = await this.step5Component.submit();
          this.setPricingFormValid(isValid);
          break;

        case 5:
          isValid = await this.step6Component.submit();
          this.setAdditionalQuestionsFormValid(isValid);
          break;

        case 6:
          isValid = true;
          break;
      }
    } catch (e) {
      this.stepLoading = false;
      throw e;
    }

    if (isValid) {
      this.nextStep();
    }
  }

  get isLastStep(): boolean {
    return this.currentStepIndex === this.steps.length - 1;
  }

  get isFirstStep(): boolean {
    return this.currentStepIndex === 0;
  }

  get isHandset$() {
    return this.layoutService.isHandset$;
  }

  private nextStep(): void {
    if (
      this.steps[this.currentStepIndex].isValid &&
      !this.isLastStep
    ) {
      do {
        this.currentStepIndex++;
      } while (
        !this.steps[this.currentStepIndex].isRelevant &&
        !this.isLastStep
      );
    }
  }

  previousStep(): void {
    this.stepLoading = true;
    if (!this.isFirstStep) {
      do {
        this.currentStepIndex--;
      } while (
        !this.steps[this.currentStepIndex].isRelevant &&
        !this.isFirstStep
      );
    }
  }

  loadingStepComplete(): void {
    // Set timeout to avoid ExpressionChangedAfterItHasBeenCheckedError
    setTimeout(() => {
      this.stepLoading = false;
    });
  }

  setProfileCheckFormValid(value: boolean): void {
    this.steps[0].isValid = value;
  }

  setRequirementsCheckFormValid(value: boolean): void {
    this.steps[1].isValid = value;
  }

  setTimeSlotFormValid(value: boolean): void {
    this.steps[3].isValid = value;
  }

  setPricingFormValid(value: boolean): void {
    this.steps[4].isValid = value;
  }

  setAdditionalQuestionsFormValid(value: boolean): void {
    this.steps[5].isValid = value;
  }

  doPayment(): void {
    this.isPayBtnClicked = true;

    const modalRef = this.modalService.show(
      LoadingDialogComponent,
      {
        class: 'modal-sm',
        ignoreBackdropClick: true,
      }
    );

    this.bookingService
      .doPayment(this.bookingId)
      .finally(() => {
        modalRef.hide();
        this.isPayBtnClicked = false;
      });
  }

  deleteBooking(): void {
    this.bookingService
      .deleteBooking(this.bookingId)
      .then(() => {
        this.bookingService.formState = {
          loading: false,
          item: BookingService.factoryItem(),
        };
      })
      .then(() => this.router.navigate(['/home']))
      .catch((e) => {
        if (e.code === 409) {
          this.note.error(
            'error.notification-payment-started'
          );
        } else {
          this.note.error('error.general');
        }
        return Promise.reject(e);
      });
  }

  get bookingId(): number {
    return this.bookingService.formState.item.bookingId;
  }

  get examIdParam(): number {
    return Number(
      this.activatedRoute.snapshot.paramMap
        .get('id')
        ?.match(/^\d+/i)
    );
  }

  get examTestName(): string {
    const examName =
      this.examService.listState.response.items.find(
        (exam) => exam.id === this.examIdParam
      )?.testname;
    return examName ?? '';
  }
}
